import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { EditorState, Modifier, DraftHandleValue, convertToRaw, ContentState } from 'draft-js';

import Editor from '@draft-js-plugins/editor';
import createEmojiPlugin from '@draft-js-plugins/emoji';
import Tippy from '@tippyjs/react';

import draftToHtml from 'draftjs-to-html';
import { stateFromHTML } from 'draft-js-import-html';

// import 'emoji-mart/css/emoji-mart.css';
import 'draft-js/dist/Draft.css';
import '@draft-js-plugins/emoji/lib/plugin.css';

import './styles.scss';
import { BiError, BiInfoCircle } from 'react-icons/bi';
import { BsCheck } from 'react-icons/bs';

const emojiPlugin = createEmojiPlugin({
  useNativeArt: false,
});
const { EmojiSuggestions, EmojiSelect } = emojiPlugin;

interface TextEditorProps {
  initialValue?: string;
  minLength?: number;
  maxLength?: number;
  className?: string;
  onChange: (content: string, contentLength?: { textLength: number; fullLength: number }) => void;
  disableEmoji?: boolean;
}

function TextEditor({ initialValue, minLength, maxLength, className, onChange, disableEmoji }: TextEditorProps) {
  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(initialValue ? stateFromHTML(initialValue) : ContentState.createFromText(initialValue || '')),
  );

  const [characters, setCharacters] = useState<{ textLength: number; fullLength: number }>({ textLength: 0, fullLength: 0 });

  const editor = useRef<Editor>(null);

  const textLength = (plainText: string, rawContent: any) => {
    let text =
        typeof plainText === 'string'
          ? plainText
              .replace(/(\n|&nbsp;)/gi, ' ')
              .replace(/\s+/gi, ' ')
              .trim()
          : '',
      countEmoji = 0,
      obj = {
        textLength: 0,
        fullLength: 0,
      };

    if (text.length <= 0) {
      setCharacters(obj);
      return obj;
    }

    if (rawContent?.entityMap[0]?.type) {
      Object.keys(rawContent?.entityMap).forEach(key => {
        const obj = rawContent?.entityMap[key];

        if (obj?.type === 'emoji' && obj?.data?.emojiUnicode) {
          countEmoji++;
          text = text.replace(obj?.data?.emojiUnicode, '');
        }
      });
    }

    obj = {
      textLength: text
        .replace(/(\n|&nbsp;)/gi, ' ')
        .replace(/\s+/gi, ' ')
        .trim().length,
      fullLength:
        text
          .replace(/(\n|&nbsp;)/gi, ' ')
          .replace(/\s+/gi, ' ')
          .trim().length + countEmoji,
    };

    setCharacters(obj);
    return obj;
  };

  const onStateChange = useCallback(
    (state: EditorState) => {
      const content = state.getCurrentContent();
      const rawContent = convertToRaw(content);
      const html = draftToHtml(rawContent);

      setEditorState(state);
      onChange(html, textLength(content.getPlainText(), rawContent));
    },
    [onChange],
  );

  const handlePaste = (text: string): DraftHandleValue => {
    const contentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      text,
      editorState.getCurrentInlineStyle(),
    );
    onStateChange(EditorState.push(editorState, contentState, 'insert-characters'));
    return 'handled';
  };

  return (
    <div className={`flex flex-col h-full relative ${className || ''}`}>
      <div className="bg-maastricht-blue flex-1 overflow-y-auto mb-2 rounded-md p-2" onClick={() => editor.current?.focus()}>
        <Editor
          editorState={editorState}
          ariaLabel="Caixa de texto. em branco . Campo editável. O texto deverá ter ao menos 100 caracteres"
          onChange={onStateChange}
          tabIndex={0}
          plugins={[emojiPlugin]}
          handlePastedText={handlePaste}
          ref={editor}
        />
      </div>
      <div className={`flex ${disableEmoji ? 'justify-end' : 'justify-between'} items-center`}>
        {disableEmoji ? (
          ''
        ) : (
          <div className="text-black relative">
            <EmojiSuggestions />
            <EmojiSelect closeOnEmojiSelect />
          </div>
        )}
        <div>
          {!!minLength || !!maxLength ? (
            <Tippy
              allowHTML={true}
              className="bg-black"
              maxWidth="none"
              content={
                <div style={{ width: 440 }} className="font-light p-3 space-y-3">
                  {!!minLength && (
                    <p
                      className={`m-0 flex items-center space-x-1 font-thin ${
                        characters.textLength < minLength ? 'text-red-200' : 'text-green-200'
                      }`}
                    >
                      {characters.textLength < minLength ? <BiError size={18} /> : <BsCheck size={18} />}{' '}
                      <span className="whitespace-nowrap">Tamanho de texto mínimo - sem contabilizar emojis:</span>
                      <strong>{minLength}</strong>
                    </p>
                  )}
                  {!!maxLength && (
                    <p
                      className={`m-0 flex items-center space-x-1 font-thin ${
                        characters.fullLength > maxLength
                          ? 'text-red-200'
                          : !!minLength && characters.textLength >= minLength
                            ? 'text-green-200'
                            : ''
                      }`}
                    >
                      {characters.fullLength > maxLength ? (
                        <BiError size={18} />
                      ) : !!minLength && characters.textLength >= minLength ? (
                        <BsCheck size={18} />
                      ) : null}
                      <span className="whitespace-nowrap">Tamanho de texto máximo:</span>
                      <strong>{maxLength}</strong>
                    </p>
                  )}
                </div>
              }
            >
              <p className="m-0 flex items-center space-x-1">
                <span>Caracteres:</span>{' '}
                <span>
                  {characters.fullLength}
                  {maxLength && '/' + maxLength}
                </span>
                <BiInfoCircle className="hidden lg:block relative -mt-1" />
              </p>
            </Tippy>
          ) : (
            <p className="m-0 block">
              <span>Caracteres:</span> {characters.fullLength}
              {maxLength && '/' + maxLength}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default TextEditor;
